import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// PrimeReact components
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';

// Types
import { MenuItem } from '../../types';

// Styles
import './Layout.css';
import { ToastContext } from '../../context/ToastContext';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [healthStatus] = useState<'healthy' | 'warning' | 'error'>('healthy');
  const toastRef = useRef<Toast>(null);

  // Navigation menu items
  const menuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      to: '/dashboard',
    },
    {
      label: 'Generate Scenarios',
      icon: 'pi pi-plus-circle',
      to: '/generate',
    },
    {
      label: 'View Results',
      icon: 'pi pi-chart-bar',
      to: '/results',
    },
    {
      label: 'Favorite Scenarios',
      icon: 'pi pi-star',
      to: '/favorites',
    },
  ];

  // Convert menu items for PrimeReact Menubar
  const primeMenuItems = menuItems.map(item => ({
    label: item.label,
    icon: item.icon,
    command: () => navigate(item.to),
    className: location.pathname === item.to ? 'active-menu-item' : '',
    template: (menuItem: any, options: any) => (
      <a className={options.className} onClick={options.onClick} role="menuitem">
        <span className={options.iconClassName} />
        <span className={options.labelClassName}>{menuItem.label}</span>
      </a>
    ),
  }));

  // Header start content (logo/brand)
  const start = (
    <div className="flex align-items-center">
      <i className="pi pi-chart-line text-2xl mr-2 text-primary"></i>
      <span className="text-xl font-bold text-primary">ScenarioIQ</span>
    </div>
  );

  // Header end content (user actions)
  const end = (
    <div className="flex align-items-center gap-2">
      {/* <SystemStatusIcon /> */}
      <Button
        icon="pi pi-cog"
        className="p-button-text p-button-rounded"
        tooltip="Account Settings"
        tooltipOptions={{ position: 'bottom' }}
        onClick={() => navigate('/account-settings')}
      />
      <Button
        icon="pi pi-sign-out"
        className="p-button-text p-button-rounded"
        tooltip="Logout"
        tooltipOptions={{ position: 'bottom' }}
        onClick={() => {
          onLogout();
        }}
      />
      <Button
        icon="pi pi-bars"
        className="p-button-text p-button-rounded md:hidden"
        onClick={() => setSidebarVisible(true)}
        tooltip="Menu"
        tooltipOptions={{ position: 'bottom' }}
      />
    </div>
  );

  // Get current page title
  const getCurrentPageTitle = (): string => {
    const currentItem = menuItems.find(item => item.to === location.pathname);
    return currentItem?.label || 'ScenarioIQ';
  };

  return (
    <div className="main-layout">
      {/* Header */}
      <div className="layout-header">
        <Menubar
          model={primeMenuItems}
          start={start}
          end={end}
          className="border-none shadow-2"
        />
      </div>

      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="w-20rem"
      >
        <div className="p-3">
          <h3 className="text-primary mb-4">
            <i className="pi pi-chart-line mr-2"></i>
            ScenarioIQ
          </h3>
          <div className="flex flex-column gap-2">
            {menuItems.map((item, index) => (
              <Button
                key={index}
                label={item.label}
                icon={item.icon}
                className={`p-button-text justify-content-start ${location.pathname === item.to ? 'p-button-primary' : ''
                  }`}
                onClick={() => {
                  navigate(item.to);
                  setSidebarVisible(false);
                }}
              />
            ))}
          </div>
        </div>
      </Sidebar>

      {/* Main Content */}
      <div className="layout-content">
        <div className="content-wrapper">
          {/* Page Content */}
          <div className="page-content">
            <ToastContext.Provider value={toastRef}>
              {children}
            </ToastContext.Provider>
          </div>
        </div>
      </div>

      {/* Single global Toast — use useToast() to access from any page */}
      <Toast ref={toastRef} />
    </div>
  );
};

export default Layout;
